import {
  Adapt,
  Anchor,
  Dialog,
  interaction,
  isWeb,
  Sheet,
  SizableText,
  styled,
  Unspaced,
  useDidFinishSSR,
  useMedia,
  XStack,
  YStack,
} from '@mythical/ui'
import {
  IconClose,
  IconEmailCircle,
  IconFacebookCircle,
  IconRedditCircle,
  IconTwitterCircle,
} from 'app/components/primitive/Icon'
import React from 'react'
import { Pressable } from 'react-native'

import { ShareModalProps } from './ShareModalProps'
import { useOnKeyPress } from 'app/hooks/use-on-key-press'
import { copyToClipboard } from 'app/lib/copy-to-clipboard'

const links = [
  {
    icon: IconFacebookCircle,
    label: 'Facebook',
    href: 'https://www.facebook.com/sharer/sharer.php?u=',
  },
  {
    icon: IconTwitterCircle,
    label: 'Twitter',
    href: 'https://twitter.com/intent/tweet?url=',
  },
  {
    icon: IconRedditCircle,
    label: 'Reddit',
    href: 'https://www.reddit.com/submit?url=',
  },
  {
    icon: IconEmailCircle,
    label: 'Email',
    href: 'mailto:?subject=Check out this Mythical Society video&body=',
  },
]

const CopyField = styled(XStack, {
  position: 'relative',
  ai: 'center',
  py: '$2',
  px: '$3',
  bg: '$bgPrimary',
  flexShrink: 1,
  borderWidth: 1,
  borderColor: '$bgTertiary',
  borderRadius: '$3',
  gap: '$2',
  jc: 'space-between',
  maxWidth: 600,
  w: '100%',
  mx: 'auto',
})

const ShareModal = ({ trigger, href }: ShareModalProps) => {
  const [open, setOpen] = React.useState(false)
  const [copied, setCopied] = React.useState(false)
  const media = useMedia()
  const didSSR = useDidFinishSSR()

  useOnKeyPress('Escape', () => setOpen(false))

  const handleSelect = async () => {
    if (!isWeb) return
    const copied = await copyToClipboard(href)
    if (!copied) return
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 1500)
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Adapt when="xxs">
        <Sheet
          zIndex={200000}
          modal
          dismissOnSnapToBottom
          unmountChildrenWhenHidden
          animation="quick"
        >
          <Sheet.Overlay
            animation="quick"
            bg="$backdrop"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Sheet.Frame theme="bgSecondary">
            <Sheet.Handle bg="$borderSecondary" top="$1.5" />
            <Adapt.Contents />
          </Sheet.Frame>
        </Sheet>
      </Adapt>

      {didSSR && (
        <Dialog.Portal>
          <Dialog.Overlay
            key="share-modal-overlay"
            animation="quick"
            bg="$backdrop"
            enterStyle={{ o: 0 }}
            exitStyle={{ o: 0 }}
          />
          <Dialog.FocusScope focusOnIdle>
            <Dialog.Content
              key="share-modal-content"
              theme="bgSecondary"
              overflow="hidden"
              br="$4"
              p="$0"
              maxWidth={700}
              w="calc(100% - 32px)"
              animateOnly={['transform', 'opacity']}
              animation={[
                'quick',
                {
                  opacity: {
                    overshootClamping: true,
                  },
                },
              ]}
              enterStyle={{ opacity: 0 }}
              exitStyle={{ opacity: 0 }}
            >
              <XStack jc="center" p="$3">
                <Dialog.Title fow="600" size="$6">
                  Share via
                </Dialog.Title>
                <Unspaced>
                  <Dialog.Close cursor="pointer" pos="absolute" right="$2">
                    <IconClose size={24} color="$fgPrimary" p="$1" />
                  </Dialog.Close>
                </Unspaced>
              </XStack>
              <YStack gap="$3" p="$4">
                <XStack
                  jc="flex-start"
                  gap="$3"
                  py="$2"
                  className="overflow-x-auto"
                  $gtXxs={{ jc: 'center' }}
                >
                  {links.map((item) => {
                    const Icon = item.icon
                    return (
                      <Anchor
                        key={item.href}
                        animation="quick"
                        display="flex"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`${item.href}${href}`}
                        fd="column"
                        jc="center"
                        ai="center"
                        gap="$2"
                        {...interaction.opacity}
                      >
                        <Icon size={74} {...(media.xs && { size: 64 })} />
                        <SizableText ta="center" size="$3">
                          {item.label}
                        </SizableText>
                      </Anchor>
                    )
                  })}
                </XStack>
                <Pressable onPress={handleSelect}>
                  <CopyField>
                    <SizableText
                      className="overflow-x-auto hide-scrollbar"
                      id="share-url"
                      w="calc(100% - 55px)"
                      color="$fgSecondary"
                      size="$3"
                      whiteSpace="nowrap"
                    >
                      {href}
                    </SizableText>
                    <SizableText
                      pos="absolute"
                      bg="$bgPrimary"
                      px="$3"
                      right="$0"
                      top="50%"
                      y="-50%"
                      size="$3.5"
                      tt="uppercase"
                      ta="center"
                      animation="quick"
                      color={copied ? '$fgSuccess' : '$fgBrand'}
                      hoverStyle={{
                        color: copied ? '$fgSuccess' : '$fgPrimary',
                      }}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </SizableText>
                  </CopyField>
                </Pressable>
              </YStack>
            </Dialog.Content>
          </Dialog.FocusScope>
        </Dialog.Portal>
      )}
    </Dialog>
  )
}

export default ShareModal
